import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useNewPreemieContext } from '../NewPreemieContext';
import { loadCurrentState } from '../storage';
import { useCheckboxWithTimestamp } from '../hooks/useCheckboxWithTimestamp';

export const useIntubationEquipmentStatus = () => {
  const saved = loadCurrentState();
  const equipment = saved?.intubationEquipment || {};
  const states = [
    equipment.laryngoscope,
    equipment.blade,
    equipment.tubes,
    equipment.nasalIntubation,
    equipment.oralIntubation,
    equipment.lubricant,
    equipment.fixation,
    equipment.portableVentilator,
    equipment.portableMonitor
  ];
  const checked = states.filter(Boolean).length;
  return { checked, total: 9 };
};

const TUBE_SIZE_ROWS = [
  { weight: '<800',    weightRange: [null, 799],   ga: '22-25', tubeSize: '2.5', suction: '5' },
  { weight: '800-1200', weightRange: [800, 1200],  ga: '26-28', tubeSize: '2.5', suction: '5 או 6' },
  { weight: '1201-2200', weightRange: [1201, 2200], ga: '29-34', tubeSize: '3',   suction: '6 או 8' },
  { weight: '>2200',  weightRange: [2201, null],  ga: '>34',   tubeSize: '3.5', suction: '8' },
];

const thStyle: React.CSSProperties = {
  border: '1px solid #b0bec5',
  padding: '5px 4px',
  textAlign: 'center',
};

const tdStyle: React.CSSProperties = {
  border: '1px solid #b0bec5',
  padding: '5px 4px',
  textAlign: 'center',
};

function getActiveRow(weight: number | null): number | null {
  if (weight === null) return null;
  if (weight < 800) return 0;
  if (weight <= 1200) return 1;
  if (weight <= 2200) return 2;
  return 3;
}

const IntubationEquipment: React.FC = () => {
  const { weightAssessment, gestationalAgeWeeks: gestationalAge } = useNewPreemieContext();
  const saved = loadCurrentState();
  const birthWeight = saved?.departmentAdmission?.birthWeight ?? null;
  const activeWeight = birthWeight !== null ? birthWeight : weightAssessment;
  const [showNasalDepthInfo, setShowNasalDepthInfo] = useState(false);
  const [showOralDepthInfo, setShowOralDepthInfo] = useState(false);

  const [laryngoscope, setLaryngoscope] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'laryngoscope',
    saved?.intubationEquipment?.laryngoscope || false
  );
  const [blade, setBlade] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'blade',
    saved?.intubationEquipment?.blade || false
  );
  const [tubes, setTubes] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'tubes',
    saved?.intubationEquipment?.tubes || false
  );
  const [nasalIntubation, setNasalIntubation] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'nasalIntubation',
    saved?.intubationEquipment?.nasalIntubation || false
  );
  const [oralIntubation, setOralIntubation] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'oralIntubation',
    saved?.intubationEquipment?.oralIntubation || false
  );
  const [lubricant, setLubricant] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'lubricant',
    saved?.intubationEquipment?.lubricant || false
  );
  const [fixation, setFixation] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'fixation',
    saved?.intubationEquipment?.fixation || false
  );
  const [portableVentilator, setPortableVentilator] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'portableVentilator',
    saved?.intubationEquipment?.portableVentilator || false
  );
  const [portableMonitor, setPortableMonitor] = useCheckboxWithTimestamp(
    'intubationEquipment',
    'portableMonitor',
    saved?.intubationEquipment?.portableMonitor || false
  );

  const calculateTubeSize = useMemo(() => {
    if (weightAssessment !== null) {
      if (weightAssessment < 1200) return 2.5;
      if (weightAssessment >= 1201 && weightAssessment <= 2200) return 3;
      if (weightAssessment > 2200) return 3.5;
    }
    if (gestationalAge !== null) {
      if (gestationalAge >= 22 && gestationalAge <= 28) return 2.5;
      if (gestationalAge >= 29 && gestationalAge <= 34) return 3;
      if (gestationalAge > 34) return 3.5;
    }
    return null;
  }, [weightAssessment, gestationalAge]);

  const activeRowIndex = useMemo(() => getActiveRow(activeWeight), [activeWeight]);

  const tubeSizesList = useMemo(() => {
    if (calculateTubeSize === null) return null;
    
    const allSizes = [2, 2.5, 3, 3.5];
    const currentIndex = allSizes.indexOf(calculateTubeSize);
    
    if (currentIndex === -1) return null;
    
    const sizes: number[] = [];
    
    // Add previous size if exists
    if (currentIndex > 0) {
      sizes.push(allSizes[currentIndex - 1]);
    }
    
    // Add current size
    sizes.push(calculateTubeSize);
    
    // Add next size if exists
    if (currentIndex < allSizes.length - 1) {
      sizes.push(allSizes[currentIndex + 1]);
    }
    
    return sizes;
  }, [calculateTubeSize]);

  const calculateNasalDepth = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const depth = (activeWeight / 1000) * 2 + 6;
      return depth.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const nasalDepthFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return `(${weightInKg} × 2) + 6 = ${calculateNasalDepth}`;
    }
    return '';
  }, [activeWeight, calculateNasalDepth]);

  const calculateOralDepth = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const depth = (activeWeight / 1000) + 6;
      return depth.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const oralDepthFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return `${weightInKg} + 6 = ${calculateOralDepth}`;
    }
    return '';
  }, [activeWeight, calculateOralDepth]);

  return (
    <div className="section-content-wrapper" style={{ direction: 'rtl' }}>
      {/* Tube size reference table - always visible at top */}
      <div style={{ marginBottom: '20px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {activeWeight === null && (
          <div style={{ marginBottom: '8px', color: '#888', fontSize: '0.85rem' }}>
            לא הוזן משקל לידה או הערכת משקל - שורה מומלצת לא מסומנת
          </div>
        )}
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.88rem', minWidth: '280px' }}>
          <thead>
            <tr style={{ backgroundColor: '#e8edf4' }}>
              <th style={thStyle}>משקל</th>
              <th style={thStyle}>שבוע הריון</th>
              <th style={thStyle}>גודל טובוס (ס"מ)</th>
              <th style={thStyle}>קטטר סקשן</th>
            </tr>
          </thead>
          <tbody>
            {TUBE_SIZE_ROWS.map((row, i) => (
              <tr
                key={i}
                style={{
                  fontWeight: activeRowIndex === i ? 'bold' : 'normal',
                  backgroundColor: activeRowIndex === i ? '#dceeff' : 'transparent'
                }}
              >
                <td style={tdStyle}>{row.weight}</td>
                <td style={tdStyle}>{row.ga}</td>
                <td style={tdStyle}>{row.tubeSize}</td>
                <td style={tdStyle}>{row.suction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 1. Laryngoscope */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={laryngoscope}
            onChange={(e) => setLaryngoscope(e.target.checked)}
          />
          <span>לרינגוסקופ</span>
        </label>
      </div>

      {/* 2. Blade */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={blade}
            onChange={(e) => setBlade(e.target.checked)}
          />
          <span>להב 0, 00</span>
        </label>
      </div>

      {/* 3. Tubes with tube size */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={tubes}
            onChange={(e) => setTubes(e.target.checked)}
          />
          <span>
            {tubeSizesList !== null ? (
              <>להכין {tubeSizesList.length} טובוסים בגדלים: {tubeSizesList.join(', ')} (לפי הערכת משקל)</>
            ) : (
              <>להכין 4 טובוסים בגדלים: 2, 2.5, 3, 3.5</>
            )}
          </span>
        </label>
      </div>

      {/* 4. Nasal Tube Depth - Informational bullet */}
      <div style={{ marginRight: '20px', paddingRight: '30px', padding: '6px 0', fontSize: '0.95rem', position: 'relative' }}>
        <span style={{ fontWeight: '500' }}>עומק טובוס לאינטובציה דרך האף{' '}</span>
        <span style={{ fontWeight: '500' }}>({birthWeight !== null ? 'לפי משקל לידה' : 'לפי הערכת משקל'}):{' '}</span>
        {calculateNasalDepth !== null ? (
          <>
            <strong>{calculateNasalDepth}</strong>
            <FontAwesomeIcon
              icon={faCircleInfo}
              onClick={() => setShowNasalDepthInfo(!showNasalDepthInfo)}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                color: '#103C6E',
                fontSize: '14px'
              }}
            />
            {showNasalDepthInfo && (
              <>
                <div
                  onClick={() => setShowNasalDepthInfo(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '250px',
                    direction: 'rtl'
                  }}
                >
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                  <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>(משקל בק״ג × 2) + 6</div>
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                  <div style={{ direction: 'ltr', textAlign: 'right' }}>{nasalDepthFormula}</div>
                </div>
              </>
            )}
          </>
        ) : (
          <em>לא הוזן משקל</em>
        )}
      </div>

      {/* 5. Oral Tube Depth - Informational bullet */}
      <div style={{ marginRight: '20px', paddingRight: '30px', padding: '6px 0', fontSize: '0.95rem', position: 'relative' }}>
        <span style={{ fontWeight: '500' }}>עומק טובוס לאינטובציה דרך הפה{' '}</span>
        <span style={{ fontWeight: '500' }}>({birthWeight !== null ? 'לפי משקל לידה' : 'לפי הערכת משקל'}):{' '}</span>
        {calculateOralDepth !== null ? (
          <>
            <strong>{calculateOralDepth}</strong>
            <FontAwesomeIcon
              icon={faCircleInfo}
              onClick={() => setShowOralDepthInfo(!showOralDepthInfo)}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                color: '#103C6E',
                fontSize: '14px'
              }}
            />
            {showOralDepthInfo && (
              <>
                <div
                  onClick={() => setShowOralDepthInfo(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '250px',
                    direction: 'rtl'
                  }}
                >
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                  <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>משקל בק״ג + 6</div>
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                  <div style={{ direction: 'ltr', textAlign: 'right' }}>{oralDepthFormula}</div>
                </div>
              </>
            )}
          </>
        ) : (
          <em>לא הוזן משקל</em>
        )}
      </div>

      {/* 6. Nasal Intubation */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={nasalIntubation}
            onChange={(e) => setNasalIntubation(e.target.checked)}
          />
          <span>לאינטובציה דרך האף - מג׳היל</span>
        </label>
      </div>

      {/* 7. Oral Intubation */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={oralIntubation}
            onChange={(e) => setOralIntubation(e.target.checked)}
          />
          <span>לאינטובציה דרך הפה - guide</span>
        </label>
      </div>

      {/* 8. Lubricant */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={lubricant}
            onChange={(e) => setLubricant(e.target.checked)}
          />
          <span>חומר סיכוך</span>
        </label>
      </div>

      {/* 9. Fixation */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={fixation}
            onChange={(e) => setFixation(e.target.checked)}
          />
          <span>קיבוע</span>
        </label>
      </div>

      {/* 10. Portable Ventilator */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={portableVentilator}
            onChange={(e) => setPortableVentilator(e.target.checked)}
          />
          <span>מנשם נייד</span>
        </label>
      </div>

      {/* 11. Portable Monitor */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={portableMonitor}
            onChange={(e) => setPortableMonitor(e.target.checked)}
          />
          <span>מוניטור נייד וסנסור סטורציה מתאים</span>
        </label>
      </div>
    </div>
  );
};

export default IntubationEquipment;
